import { useState, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { AppStoreSearchResult, AppPlatform } from "@/types/application";
import { toast } from "sonner";

interface UseItunesSearchOptions {
  debounceMs?: number;
}

interface SearchParams {
  term: string;
  country: string;
  platform: AppPlatform;
}

const PLATFORM_TO_ENTITY: Record<AppPlatform, string> = {
  ios: 'software',
  ipados: 'iPadSoftware',
  macos: 'macSoftware',
  tvos: 'tvSoftware',
};

export const useItunesSearch = (options: UseItunesSearchOptions = {}) => {
  const [results, setResults] = useState<AppStoreSearchResult[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [lastSearchTerm, setLastSearchTerm] = useState("");

  const search = useCallback(async ({ term, country, platform }: SearchParams) => {
    if (!term.trim()) {
      setResults([]);
      setLastSearchTerm("");
      return;
    }

    try {
      setLoading(true);
      setError(null);
      setLastSearchTerm(term);

      const entity = PLATFORM_TO_ENTITY[platform];

      const { data, error: invokeError } = await supabase.functions.invoke('itunes-search', {
        body: { 
          term: term.trim(), 
          country, 
          entity,
          limit: 25 
        }
      });

      if (invokeError) {
        throw invokeError;
      }

      if (data?.error) {
        throw new Error(data.error);
      }

      setResults(data?.results || []);
    } catch (err) {
      const error = err instanceof Error ? err : new Error('Search failed');
      setError(error);
      toast.error(`Search failed: ${error.message}`);
      setResults([]);
    } finally {
      setLoading(false);
    }
  }, []);

  const clearResults = useCallback(() => {
    setResults([]);
    setLastSearchTerm("");
    setError(null);
  }, []);

  return {
    results,
    loading,
    error,
    lastSearchTerm,
    search,
    clearResults,
  };
};
